import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Offcanvas,
  Row,
  Spinner,
} from "react-bootstrap"
import React, { useEffect, useState, useRef, useMemo } from "react"
import { Controller, useForm, useWatch, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { IconPlus, IconTrash, IconX } from "@tabler/icons-react"
import TimezoneSelect, { allTimezones } from "react-timezone-select"
import "react-tagsinput/react-tagsinput.css"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import { toast } from "sonner"
import { getCities, getStates, getCountries } from "../../Utils/hooks/location"
import { GetRequest, PutRequest, UpdateRF } from "../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import { useNavigate } from "react-router-dom"
import { usePostRequest } from "../../Utils/hooks/QueryHandler"
import Select, { components } from "react-select"
import { customStyles, fetchTemporaryFilterData, getUpdatedFields } from "../../Utils/helpers/commonhelper"
import { UsersPopup } from "../../CommonComponents/UsersPopup"
import { CreateProductsModal } from "../../CommonComponents/CreateProductsModal"
import { ContactForm } from "../Contacts/ContactModule/ContactForm"
import { CancelBtn } from "../../CommonComponents/Buttons/CancelBtn"
import { SubmitBtn } from "../../CommonComponents/Buttons/SubmitBtn"
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials"

const formSchema = (isEditMode) =>
  z.object({
    contact_name: z.string().min(1, "Contact name is required"),
    pipeline: z.string().min(1, "Pipeline is required"),
    pipeline_stage: z
      .string()
      .nullable()
      .refine((val) => val !== null && val !== "", {
        message: "Pipeline stage is required",
      }),
    owner: z.string().min(1, "Owner is required"),
    expected_closure_date: z.string().nonempty("Date is required"),
    expected_closure_time: z.string().nonempty("Time is required"),
    requirement: z.string().optional(),
    // ?min(1, "Requirement is required"),
    priority: z.string().min(1, "Priority is required"),
    product_services: z
      .array(
        z.object({
          product_id: z.string().optional(),
          // min(1, "Product is required"),
          quantity: z.coerce
            .number()
            .min(0, "Quantity cannot be negative")
            .optional(),
          // min(1, "Quantity must be at least 1"),
          budget: z.preprocess(
            (val) =>
              typeof val === "number" && Number.isNaN(val) ? undefined : val,
            z
              .number({
                required_error: "Budget is required",
                invalid_type_error: "Budget is required",
              })
              // .int()
              .optional()
            // .min(0, "Budget must be 0 or more"),
          ),
        }),
      ).optional(),
    // .min(1, "At least one product item is required"),
    lead_source: isEditMode
      ? z.string().optional()
      : z.string().min(1, "Lead source is required"),
    follow_up_date: isEditMode
      ? z.string().optional()
      : z.string().min(1, "Date is required"),
    follow_up_time: isEditMode
      ? z.string().optional()
      : z.string().min(1, "Time is required"),
    feedback: isEditMode
      ? z.string().optional()
      : z
        .string()
        .min(1, "Feedback is required")
        .min(5, "Feedback is too short (minimum 5 characters)"),
  })

const getCurrentTime = () => {
  const now = new Date()
  return now.toTimeString().slice(0, 5)
}

//menulist for add new products and services button in dropdown
const MenuList = (props) => {
  const { show, moduleType, canCreate, onAddNew } = props?.selectProps
  return (
    <>
      <components.MenuList {...props}>
        {props.children}
        {show && (
          <div
            style={{
              padding: "10px",
              borderTop: "1px solid #ddd",
              textAlign: "center",
              cursor: "pointer",
              color: "#0d6efd",
              fontWeight: 500,
            }}
            onClick={() => {
              if (!canCreate && (moduleType === "contact" || moduleType === "lead_source" || moduleType === "product_services")) {
                toast.warning("You are not authorized for this action!")
                return
              }     // trigger your modal open here
              onAddNew()
            }}
          >
            + Add New
          </div>
        )}
      </components.MenuList>
    </>
  )
}

// Dynamic Product Fields Component with forwardRef
const DynamicProductFields = React.forwardRef(
  (
    {
      control,
      register,
      setValue,
      watch,
      clearErrors,
      trigger,
      errors,
      products,
      setShowModal,
      leadMasterGrant
    },
    ref,
  ) => {
    const { fields, append, remove } = useFieldArray({
      control,
      name: "product_services",
    })

    // Add initial field if none exist
    React.useEffect(() => {
      if (fields.length === 0) {
        append({ product_id: "", quantity: 1, budget: 0 })
      }
    }, [fields.length, append])

    // Expose method to handle new product from modal
    React.useImperativeHandle(ref, () => ({
      addNewProductFromModal: (productId, price) => {
        const currentFields = watch("product_services") || []

        // Check if there's only one field and it's empty
        const isOnlyDefaultField =
          currentFields.length === 1 &&
          (!currentFields[0].product_id || currentFields[0].product_id === "")

        if (isOnlyDefaultField) {
          // Set to the first field if it's empty
          setValue(`product_services.0.product_id`, String(productId))
          setValue(`product_services.0.budget`, Number(price), {
            shouldValidate: true,
          })
          clearErrors(`product_services.0.budget`)
        } else {
          // Add as new field if products already exist
          append({
            product_id: String(productId),
            quantity: 1,
            budget: Number(price),
          })
        }
      },
    }))

    const addMoreFields = () => {
      append({ product_id: "", quantity: 1, budget: 0 })
    }

    const removeFields = (index) => {
      if (fields.length > 1) {
        remove(index)
      }
    }
    const fieldsArr = useWatch({ control, name: "product_services" }) || []
    // const getFilteredOptions = (currentIndex) => {
    //   const selectedProductIds = fieldsArr
    //     ?.map((item, idx) => (idx !== currentIndex ? item?.product_id : null))
    //     ?.filter(Boolean);

    //   return products.filter(
    //     (option) => !selectedProductIds.includes(String(option.value))
    //   );
    // };

    return (
      <div>
        {fields.map((field, index) => (
          <div key={field.id} className={index > 0 ? "" : ""}>
            <Row>
              {/* Product Selection */}
              <Col md={6}>
                <Form.Group
                  className="mb-3 focus-z-index position-relative"
                  controlId={`product-${index}`}
                >
                  <Form.Label>
                    Product and Services
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Controller
                    name={`product_services.${index}.product_id`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        className="basic-single"
                        options={products.map((product) => ({
                          value: product.id,
                          label: product.service,
                        }))}
                        value={
                          products.length
                            ? products.find(
                              (p) => String(p.id) === String(field.value),
                            )
                              ? {
                                value: field.value,
                                label: products.find(
                                  (p) => String(p.id) === String(field.value),
                                )?.service,
                              }
                              : null
                            : null
                        }
                        onChange={(selectedOption) => {
                          const selectedId = selectedOption
                            ? selectedOption.value
                            : ""
                          field.onChange(selectedId)

                          if (selectedId) {
                            const selectedProduct = products.find(
                              (p) => String(p.id) === String(selectedId),
                            )
                            if (selectedProduct) {
                              setValue(
                                `product_services.${index}.budget`,
                                Number(selectedProduct.price),
                                { shouldValidate: true },
                              )
                              clearErrors(`product_services.${index}.budget`)
                            }
                          } else {
                            setValue(`product_services.${index}.budget`, 0, {
                              shouldValidate: false,
                            })
                            clearErrors(`product_services.${index}.budget`)
                          }
                        }}
                        styles={customStyles}
                        isSearchable={true}
                        isClearable={true}
                        classNamePrefix="react-select"
                        components={{ MenuList }}
                        canCreate={leadMasterGrant?.isCreate}
                        moduleType={'product_services'}
                        show={true}
                        onAddNew={() => setShowModal(true)}
                        placeholder="Select Product and services"
                      />
                    )}
                  />
                  {errors.product_services?.[index]?.product_id && (
                    <span className="text-danger fs-12 mt-1">
                      {errors.product_services[index].product_id.message}
                    </span>
                  )}
                </Form.Group>
              </Col>
              {/* Quantity */}
              <Col md={3}>
                <Form.Group
                  className="mb-3 focus-z-index position-relative"
                  controlId={`quantity-${index}`}
                >
                  <Form.Label>
                    Quantity
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="Enter Quantity"
                    {...register(`product_services.${index}.quantity`, { require: false })}
                  />
                  {errors.product_services?.[index]?.quantity && (
                    <span className="text-danger fs-12 mt-1">
                      {errors.product_services[index].quantity.message}
                    </span>
                  )}
                </Form.Group>
              </Col>
              {/* Budget */}
              <Col md={3}>
                <Form.Group
                  className="mb-3 d-flex align-items-center  focus-z-index position-relative"
                  controlId={`budget-${index}`}
                >
                  <div className="w-100">
                    <Form.Label>
                      Budget
                      {/* <span className="text-danger">*</span> */}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter Budget"
                      step="0.01"
                      {...register(`product_services.${index}.budget`, {
                        require: false,
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                    />
                    {errors.product_services?.[index]?.budget && (
                      <span className="text-danger fs-12 mt-1">
                        {errors.product_services[index].budget.message}
                      </span>
                    )}
                  </div>

                  {/* Remove button (only for rows after first) */}
                  {index > 0 && (
                    <button
                      type="button"
                      style={{
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "0.4px solid red",
                        borderRadius: "50%",
                        cursor: "pointer",
                        marginLeft: "8px",
                        marginBottom: "4px",
                      }}
                      onClick={() => removeFields(index)}
                    >
                      <IconTrash
                        stroke={2}
                        style={{ stroke: "red" }}
                        className="w-3 h-3"
                      />
                    </button>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        ))}

        {/* Add More Button */}
        <div className="text-end mb-3">
          <button
            type="button"
            style={{
              fontSize: "14px",
              border: 0,
              color: "#777",
              background: "none",
            }}
            onClick={addMoreFields}
          >
            Add Product
          </button>
          <IconPlus
            stroke={2}
            style={{ width: "17px", height: "17px" }}
          ></IconPlus>
        </div>

        {/* Show validation error for the entire array */}
        {errors.product_services && !Array.isArray(errors.product_services) && (
          <div className="text-danger text-center fs-12 mt-1">
            {errors.product_services.message}
          </div>
        )}
      </div>
    )
  },
)

const getTomorrowDate = () => {
  const t = new Date()
  t.setDate(t.getDate() + 1)
  return t.toISOString().slice(0, 10)
}

export const CreateLead = ({
  show,
  onHide,
  editId = null,
  stageId = null,
  module_name,
  pipelineId = null,
  refetchLeads,
  setLeadClose,
  // while creating / editing lead from contact
  contactId,
  // needed while adding from companies
  contactArray,

  currAdvFilter,
  applyFilterDataParams,
  setCurrAdvFilter,
  setFilterState,
  setTempFilterData,
  setCustomFilter,
  setLeadsFilterValues,
}) => {

  // Session storage and authentication setup
  const { customer, setCustomer, headers, extras, navigate, customerId: customer_id, loginUserId, userType } = useGetEssentials()

  // Main create/edit lead form component
  const [contactDetails, setContactDetails] = useState(null)
  const [contactOptions, setContactOptions] = useState([])
  const [selectedContactId, setSelectedContactId] = useState("")
  const [ownerOptions, setOwnerOptions] = useState([])
  const [isEditMode, setIsEditMode] = useState(false)
  // editleaddata state to pass the data fetched fields to the commonhelper function to identify the changed fields
  const [editLeadData, setEditLeadData] = useState(null)
  // const [latestProductId, setLatestProductId] = useState(null)

  const [country, setCountries] = useState([{ countryName: "", countryId: 0 }])
  const [states, setStates] = useState([{ stateName: "", stateCode: 0 }])
  const [city, setCity] = useState([{ cityName: "", cityCode: 0 }])
  const [products, setProducts] = useState([])
  const [showModal, setShowModal] = useState(false)

  const [leadSource, setLeadSource] = useState([])
  const [pipeLine, setPineLineStage] = useState([])
  const [isContactSelected, setIsContactSelected] = useState(false)
  const [expectedDate, setExpectedDate] = useState("")
  const [expectedTime, setExpectedTime] = useState("")
  const [hasFormChanges, setHasFormChanges] = useState(false)
  const [originalFormData, setOriginalFormData] = useState(null)
  const [isOwnerSame, setIsOwnerSame] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDataFetching, setIsDataFetching] = useState(false)
  const [selectedPipelineId, setSelectedPipelineId] = useState(
    pipelineId || null,
  )
  const [availablePipelines, setAvailablePipelines] = useState([])

  // new contact add form state
  const [contactShow, setShowContact] = useState(false)

  // new lead source form show
  const [sourceShow, setSourceShow] = useState(false)
  const [recentSource, setRecentSource] = useState({
    recentSourceId: "",
    recentSourceName: "",
  })

  // state for recent contact added directly from add new
  const [recentContact, setRecentContact] = useState({
    recentContactId: "",
    recentContactName: "",
  })

  // Create ref for DynamicProductFields
  const productFieldsRef = useRef(null)

  const loginUserName = `${customer?.customer_details?.first_name || ""} ${customer?.customer_details?.last_name || ""
    }`
  // toggle state for hiding the unrequired sections
  const [showExtraSections, setShowExtraSections] = useState(true)

  const priorityOptions = [
    { id: "low", label: "Low" },
    { id: "medium", label: "Medium" },
    { id: "high", label: "High" },
  ]

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(formSchema(isEditMode)),
    defaultValues: {
      owner: loginUserId,
      product_services: [],
      expected_closure_date: getTomorrowDate(),
      expected_closure_time: getCurrentTime(),
      follow_up_date: getTomorrowDate(),
      follow_up_time: getCurrentTime(),
      priority: priorityOptions[0].id,
    },
  })

  const [PERMISSION, _] = useLocalStorageState('permission')
  const leadGrant = PERMISSION.leads
  const companyGrants = PERMISSION.company
  const leadContactGrant = PERMISSION.contact
  const leadMasterGrant = PERMISSION.setting.feature.master

  const customContactOptions = useMemo(
    () =>
      contactOptions.map((conta) => ({
        value: conta.id,
        label: conta.label,
        company_name: conta.company,
      })),
    [contactOptions],
  )
  // Custom option component for better formatting (optional)
  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <div>
          <div>{props.data.label}</div>
          {props.data.company_name && (
            <div style={{ fontSize: "0.85em", color: "#666" }}>
              {props.data.company_name}
            </div>
          )}
        </div>
      </components.Option>
    )
  }

  // Convert 12-hour time format to 24-hour format
  const convert12HourTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(" ")
    let [hours, minutes] = time.split(":")

    if (hours === "12") {
      hours = "00"
    }

    if (modifier === "PM") {
      hours = Number.parseInt(hours, 10) + 12
    }

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`
  }
  const selectedContactName = useWatch({ control, name: "contact_name" })
  const feedbackValue = useWatch({ control, name: "feedback" })

  // Watch all form values to detect changes
  const watchedValues = watch()

  // Check if form has changes compared to original data
  useEffect(() => {
    if (originalFormData && isEditMode) {
      const hasChanges = Object.keys(originalFormData).some((key) => {
        if (key === "product_services") {
          return (
            JSON.stringify(watchedValues[key]) !==
            JSON.stringify(originalFormData[key])
          )
        }
        return watchedValues[key] !== originalFormData[key]
      })
      setHasFormChanges(hasChanges)
    } else if (!isEditMode) {
      // For new leads, check if any field has been filled
      const hasData = Object.keys(watchedValues).some((key) => {
        const value = watchedValues[key]
        if (key === "product_services") {
          return (
            value &&
            value.length > 0 &&
            value.some(
              (item) => item.product_id || item.quantity || item.budget,
            )
          )
        }
        return value && value !== "" && key !== "owner"
      })
      setHasFormChanges(hasData)
    }
  }, [watchedValues, originalFormData, isEditMode])

  // Clear errors when fields are properly filled
  useEffect(() => {
    if (feedbackValue && feedbackValue.length >= 5) {
      clearErrors("feedback")
    }
  }, [feedbackValue, clearErrors])

  // Fetch available pipelines
  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const res = await GetRequest(
          `lead/fetchPipelines_dropdown/${customer_id}/leads/${loginUserId}/${userType}`,
          { headers },
          extras,
        )
        if (res?.status === "success") {
          setAvailablePipelines(res.data || [])

          // Only set default pipeline if no pipeline is selected and it's not edit mode
          if (!selectedPipelineId && !editId && res.data?.length > 0 && module_name === "leads") {
            const activePipeline =
              res.data.find((p) => p.is_active === "yes") || res.data[0]
            setSelectedPipelineId(activePipeline.id)
            setValue("pipeline", String(activePipeline.id))
          }
        }
      } catch (error) {
        return
      }
    }

    if (show && customer_id) {
      fetchPipelines()
    }
  }, [show, customer_id, editId])

  const fetchLeadById = async () => {
    if (!editId && !contactId) return
    setIsDataFetching(true)
    try {
      const res = await GetRequest(
        `/lead/getLeadsDetailsById/${editId}/${customer_id}/${loginUserId}`,
        { headers },
        extras,
      )
      if (res?.status === "success") {
        const leadData = res.data[0]
        setEditLeadData(leadData)
        if (leadData) {
          setIsEditMode(true)
          if (
            userType === "1"
          ) {
            setIsOwnerSame(true)
          } else if (loginUserId === leadData?.owner_id) {
            setIsOwnerSame(true)
          } else {
            setIsOwnerSame(false)
          }

          const contactId = String(leadData.contact_id)
          setSelectedContactId(contactId)
          setIsContactSelected(true)

          // Set pipeline ID from lead data
          const leadPipelineId = leadData.pipeline_id || pipelineId
          setSelectedPipelineId(leadPipelineId)

          setExpectedDate(leadData.expected_closure_date)
          setExpectedTime(convert12HourTo24Hour(leadData.expected_closure_time))
          const parsedProducts = JSON.parse(leadData?.product_services)

          const formData = {
            contact_name: leadData.contact_id,
            // owner: leadData.added_by,
            owner: leadData.owner_id,
            pipeline: String(leadPipelineId),
            pipeline_stage: leadData.pipeline_stage_id,
            expected_closure_date: leadData.expected_closure_date,
            expected_closure_time: convert12HourTo24Hour(
              leadData.expected_closure_time,
            ),
            requirement: leadData.requirement,
            lead_source: leadData.lead_source_id,
            follow_up_date: leadData.followup_date,
            follow_up_time: leadData.followup_time,
            feedback: "",
            product_services: parsedProducts,
            priority: leadData.priority || "low",
          }

          // Store original data for change detection
          setOriginalFormData(formData)
          reset(formData)
          clearErrors()

          setTimeout(() => {
            fetchContactDetailById(contactId)
          }, 200)
        }
      }
    } catch (err) {
      toast.error("Failed to fetch lead")
    } finally {
      setIsDataFetching(false)
    }
  }

  // Initialize form based on edit mode and stage selection
  useEffect(() => {
    if (editId) {
      fetchLeadById()
    } else if (contactId) {
      setIsContactSelected(true)
    } else if (!recentContact.recentContactId && !recentSource.recentSourceId) {
      setIsEditMode(false)
      setIsContactSelected(false)
      setSelectedContactId("")
      setContactDetails(null)
      setExpectedDate(getTomorrowDate())
      setExpectedTime(getCurrentTime())
      setHasFormChanges(false)
      setOriginalFormData(null)

      // const firstOwnerId =
      //   ownerOptions.length > 0 ? ownerOptions[0].id : loginUserId
      const defaultValues = {
        owner: loginUserId,
        pipeline: pipelineId,
        pipeline_stage: stageId ? String(stageId) : null,
        contact_name: "",
        expected_closure_date: getTomorrowDate(),
        expected_closure_time: getCurrentTime(),
        requirement: "",
        product_services: [],
        lead_source: "",
        follow_up_date: getTomorrowDate(),
        follow_up_time: getCurrentTime(),
        priority: priorityOptions[0].id,
        feedback: "",
      }

      reset(defaultValues)

      if (pipelineId && !selectedPipelineId) {
        setSelectedPipelineId(pipelineId)
        setValue("pipeline", pipelineId)
      }
    }
  }, [
    editId,
    stageId,
    reset,
    ownerOptions,
    recentContact.recentContactId,
    recentSource.recentSourceId,
    isOwnerSame
  ])

  useEffect(() => {
    setIsContactSelected(!!selectedContactName && selectedContactName !== "")
  }, [selectedContactName])


  // Scroll to specific section in form
  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      const formContainer = document.querySelector(".form-container")
      formContainer.scrollTo({
        top: element.offsetTop - 110,
        behavior: "smooth",
      })
    }
  }

  const { mutate: addLead } = usePostRequest(
    `lead/addLeads`,
    null,
    { headers },
    extras,
  )
  const changedPipelineId = watch("pipeline")
  const fecthPipeLine = async () => {
    try {
      if (changedPipelineId === null) return
      const res = await GetRequest(
        `/lead/fetchPipelineStage/${changedPipelineId}/${customer_id}`,
        { headers },
        extras,
      )
      if (res.status === false) return
      if (res.status === "success") setPineLineStage(res.data)
    } catch (err) {
      return
    }
  }

  // Refetch stages on pipeline change; stage clear is handled in pipeline Select onChange
  useEffect(() => {
    if (changedPipelineId) {
      fecthPipeLine()
    }
  }, [changedPipelineId])

  // Handle form submission
  const onSubmit = async (data) => {
    // if (!isValid) return
    // Only proceed if there are changes in edit mode
    if (isEditMode && !hasFormChanges) {
      handleClose()
      return
    }
    setIsSaving(true)
    try {
      const commonPayload = {
        customer_id: customer_id,
        contact_id: selectedContactId,
        pipeline_id: selectedPipelineId,
        pipeline_stage_id: data.pipeline_stage,
        owner_id: data.owner,
        expected_closure_date: data.expected_closure_date,
        expected_closure_time: data.expected_closure_time,
        product_services: data.product_services,
        lead_source_id: data.lead_source,
        requirement: data.requirement,
        user_login_id: loginUserId,

      }
      let res
      if (editId) {
        const updatePayload = {
          ...commonPayload,
          lead_id: editId,
        }
        const finalPayload = {
          ...updatePayload,
          updating_field: getUpdatedFields(editLeadData, updatePayload)
        }
        res = await PutRequest(
          `/lead/updateLead`,
          updatePayload,
          { headers },
          extras,
        )
        if (res.status === "fail" || res.success === false) {
          toast.error(res.message)
          setIsSaving(false)
          return
        }
        toast.success(res.message || "Lead updated successfully")
        // onLeadUpdated && onLeadUpdated()
        if (currAdvFilter === "apply") {
          fetchTemporaryFilterData(applyFilterDataParams?.applyFilterData)
        } else {
          setCurrAdvFilter && setCurrAdvFilter(null)
          setFilterState && setFilterState([])
          setTempFilterData && setTempFilterData(null)
          setCustomFilter && setCustomFilter(null)
          setLeadsFilterValues && setLeadsFilterValues({ filter_values: [], filter_state: [] })
          refetchLeads && refetchLeads()
        }
        handleClose()
        setIsSaving(false)

      } else {
        // setIsSaving(false)
        const createPayload = {
          ...commonPayload,
          feedback: data.feedback,
          followup_date: data.follow_up_date,
          followup_time: data.follow_up_time,
          priority: data.priority,
        }
        addLead(createPayload, {
          onSuccess: (res) => {
            if (res.status === "fail") {
              toast.error(res.message)
              setIsSaving(false)
              return
            }
            if (res.status === "error") return
            if (res.status === "Expire") {
              UpdateRF(headers.Authentication)
              navigate("/login")
              return
            }
            refetchLeads && refetchLeads()
            handleClose()
            setIsSaving(false)
            toast.success(res.message)
          },
          onError: (err) => {
            toast.error(err?.message || "add lead request failed")
            setIsSaving(false)
          },
        })
      }
    } catch (err) {
      return;
    }
  }
  // Fetch owner options when component mounts
  useEffect(() => {
    const fetchOwnerOptions = async () => {
      try {
        const res = await GetRequest(
          `/users/users/fetchUserAsOwner/${customer_id}`,
          { headers },
          extras,
        )

        let data = []

        if (res?.status === "success") {
          data = res.data.map((user) => ({
            id: user.id,
            label: user.full_name,
          }))
        }

        const isLoginUserIncluded = data.some((user) => user.id === loginUserId)

        if (!isLoginUserIncluded) {
          data.unshift({ id: loginUserId, label: loginUserName })
        }

        setOwnerOptions(data)
        // Set first owner as default
        if (data.length > 0) {
          setValue("owner", loginUserId)
        }
      } catch (error) {
        const fallback = [{ id: loginUserId, label: loginUserName }]
        setOwnerOptions(fallback)
        setValue("owner", loginUserId)
        return
      }
    }

    if (show) {
      fetchOwnerOptions()
    }
  }, [show])

  // Fetch contact options when form opens
  useEffect(() => {
    const fetchContactNames = async () => {
      const res = await GetRequest(
        `lead/getContactsForLeads/${customer_id}/${loginUserId}/${userType}`,
        headers,
        extras,
      )
      if (res.status === "fail" || res.success === false) {
        return
      }
      const data = res.data.map((contactName) => ({
        id: contactName.id,
        label: contactName.contact_full_name,
        company: contactName.company_name,
      }))

      setContactOptions(data)
      res.data &&
        res.data.find((c) => {
          if (c.id === recentContact.recentContactId) {
            setValue("contact_name", recentContact.recentContactId)
            setSelectedContactId(recentContact.recentContactId)
            setRecentContact((prev) => ({
              ...prev,
              recentContactName: c.contact_full_name,
            }))
          }
        })
    }

    if (contactId !== undefined) {
      setSelectedContactId(contactId)
      setValue("contact_name", contactId)
      fetchContactNames()
      return
    }
    if (show || recentContact.recentContactId) {
      fetchContactNames()
    }
  }, [show, recentContact.recentContactId, contactId])

  // Fetch contact details by ID
  const fetchContactDetailById = async (
    contactSelected = contactId !== undefined ? contactId : selectedContactId,
  ) => {
    if (!contactSelected) {
      setContactDetails(null)
      return
    }
    try {
      const res = await GetRequest(
        `lead/getContactDetailsForLeads/${contactSelected}`,
        headers,
        extras,
      )
      if (res.status === "success" && res.data?.length > 0) {
        setContactDetails(res.data[0])
        setIsContactSelected(true)
      }
    } catch (error) {
      return
    }
  }
  useEffect(() => {
    fetchContactDetailById()
  }, [selectedContactId, recentContact.recentContactId, show, contactId])

  // Fetch countries when component mounts
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await getCountries()
        if (data) {
          setCountries(
            data.map((c) => ({
              countryName: c.name,
              countryId: c.id,
            })),
          )
        }
      } catch (err) {
        return
      }
    }
    if (show) {
      fetchCountries()
    }
  }, [show])

  // Fetch states when country changes
  useEffect(() => {
    if (!contactDetails?.country) return

    const fetchStates = async () => {
      try {
        const data = await getStates(contactDetails.country)
        if (data) {
          setStates(
            data.map((s) => ({
              stateName: s.state_name,
              stateCode: s.id,
            })),
          )
        }
      } catch (err) {
        // Error handling
        return
      }
    }
    fetchStates()
  }, [contactDetails?.country])

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const data = await getCities(contactDetails.state)
        if (data) {
          const mapped = data.map((city) => ({
            cityName: city.city_name,
            cityCode: city.id,
          }))

          setCity(mapped)
        }
      } catch (error) {
        toast.error("Error Fetching Cities")
        return
      }
    }

    if (contactDetails?.state) fetchCities()
  }, [contactDetails?.state])

  // Helper functions to get location names
  const getCountryName = (id) => {
    const found = country.find((c) => String(c.countryId) === String(id))
    return found ? found.countryName : ""
  }

  const getStateName = (id) => {
    const found = states.find((s) => String(s.stateCode) === String(id))
    return found ? found.stateName : ""
  }

  const getCityName = (id) => {
    const found = city.find((c) => String(c.cityCode) === String(id))
    return found ? found.cityName : ""
  }

  // Fetch products when form opens
  const fetchProducts = async () => {
    try {
      const res = await GetRequest(
        `masters/getProducts/${customer_id}`,
        { headers },
        extras,
      )

      if (res?.data) {
        setProducts(res.data)
      }
    } catch (error) {
      // Error handling
      return
    }
  }

  // Fetch lead sources when form opens
  const fetchLeadSource = async () => {
    try {
      const res = await GetRequest(
        `masters/getAllLead_source/${customer_id}`,
        { headers },
        extras,
      )
      if (res?.data) setLeadSource(res.data)
      res.data &&
        res.data.find((c) => {
          if (c.id === String(recentSource.recentSourceId)) {
            setValue("lead_source", recentSource.recentSourceId)
            setRecentSource((prev) => ({
              ...prev,
              recentSourceName: c.source_title,
            }))
          }
        })
    } catch (error) {
      // Error handling
      return
    }
  }

  // Fetch products and lead sources when form opens
  useEffect(() => {
    if (customer_id && show) {
      fetchProducts()
      fetchLeadSource()
    }
  }, [customer_id, show, recentSource.recentSourceId])

  // Fetch pipeline stages when form opens
  useEffect(() => {
    if (customer_id && show) {
      fecthPipeLine()
    }
  }, [customer_id, pipelineId, show])

  // Handle form close
  const handleClose = () => {
    if (!editId) {
      const firstOwnerId =
        ownerOptions.length > 0 ? ownerOptions[0].id : loginUserId
      reset({
        owner: loginUserId,
        pipeline_stage: null,
        contact_name: "",
        expected_closure_date: getTomorrowDate(),
        expected_closure_time: getCurrentTime(),
        follow_up_date: getTomorrowDate(),
        follow_up_time: getCurrentTime(),
        priority: priorityOptions[0].id,
        requirement: "",
        product_services: [],
        lead_source: "",
        feedback: "",
      })
      setIsOwnerSame(false)
      setContactDetails(null)
      setSelectedPipelineId(null)
      setSelectedContactId("")
      setExpectedDate(getTomorrowDate())
      setExpectedTime(getCurrentTime())
      setIsContactSelected(false)
      setIsEditMode(false)
      setContactOptions([])
      setRecentContact([])
    }
    reset({
      owner: loginUserId,
      pipeline_stage: null,
      contact_name: "",
      expected_closure_date: getTomorrowDate(),
      expected_closure_time: getCurrentTime(),
      requirement: "",
      product_services: [],
      lead_source: "",
      follow_up_date: getTomorrowDate(),
      follow_up_time: getCurrentTime(),
      feedback: "",
    })
    setIsOwnerSame(false)
    // setLatestProductId(null)
    setHasFormChanges(false)
    setOriginalFormData(null)
    clearErrors()
    onHide()
  }

  // Generate sidebar items based on contact selection and edit mode
  const getSidebarItems = () => {
    const baseItems = [
      { id: "BasicInformation", label: "General Information", show: true },
    ]

    if (isContactSelected) {
      baseItems.push(
        {
          id: "Communication",
          label: "Communication",
          show: !showExtraSections ? true : false,
        },
        {
          id: "Location",
          label: "Location",
          show: !showExtraSections ? true : false,
        },
        {
          id: "Social",
          label: "Social",
          show: !showExtraSections ? true : false,
        },
        {
          id: "BussinessInformation",
          label: "Business Information",
          show: !showExtraSections ? true : false,
        },
        { id: "Requirment", label: "Requirement", show: true },
      )

      if (!isEditMode) {
        baseItems.push({
          id: "FollowUpInformation",
          label: "FollowUp Information",
          show: true,
        })
      }
    }

    return baseItems.filter((item) => item.show)
  }

  return (
    <>
      <style>
        {`
        .w-75::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .w-75::-webkit-scrollbar-track {
          background: transparent;
        }
        .w-75::-webkit-scrollbar-thumb {
          background-color: rgb(122, 58, 94);
          border-radius: 4px;
        }
        .w-75::-webkit-scrollbar-thumb:hover {
          background-color: rgb(102, 48, 79);
        }
        
        /* Fixed form container size */
        .form-container {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }
        
        /* Fixed offcanvas width */
        .offcanvas.w-75 {
          width: 75% !important;
          max-width: 75% !important;
          min-width: 75% !important;
        }
      `}
      </style>
      <Offcanvas
        className="w-75"
        placement="end"
        backdrop="static"
        enforceFocus={true}
        show={show}
        keyboard={!(editId && isDataFetching)}
      // onHide={handleClose}
      >
        <Offcanvas.Header className="border-bottom  border-dashed pt-3 pb-0 d-flex justify-content-between align-items-center w-full ">
          <div className="pt-0 ps-4">
            {editId ? (
              <>
                <Offcanvas.Title
                  style={{ color: "rgb(7, 145, 255)" }}
                  className="addEnquiryHead"
                >
                  Edit Leads
                </Offcanvas.Title>
                <p
                  style={{ fontSize: "14px", color: "#b26792" }}
                  className="text-secondary"
                >
                  Edit potential lead here.
                </p>
              </>
            ) : (
              <>
                <Offcanvas.Title
                  style={{ color: "rgb(7, 145, 255)" }}
                  className="addEnquiryHead"
                >
                  Add Leads
                </Offcanvas.Title>
                <p
                  style={{ fontSize: "14px", color: "#b26792" }}
                  className="text-secondary"
                >
                  Add potential lead here, Fill all the mandatory fields before
                  submitting the lead.
                </p>
              </>
            )}
          </div>

          {/* Toggle Switch */}
          <div className="d-flex align-items-baseline gap-2">
            <div className=" ">
              <Form.Check
                type="switch"
                id="toggle-extra"
                label="Required Fields."
                checked={showExtraSections}
                onChange={() => setShowExtraSections(!showExtraSections)}
              />
            </div>
            <button
              style={{
                cursor: editId && isDataFetching ? "not-allowed" : "pointer",
                border: "none",
                background: "transparent",
              }}
              disabled={editId && isDataFetching}
              onClick={() => {
                if (!(editId && isDataFetching)) {
                  handleClose()
                }
              }}
            >
              {" "}
              <IconX size={20} stroke={2} />
            </button>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body
          style={{
            position: "relative",
            pointerEvents: editId && isDataFetching ? "none" : "auto",
          }}
        >
          {editId && isDataFetching && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.7)",
                zIndex: 1000,
                pointerEvents: "all",
              }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}
          <div style={{ display: "flex", height: "100%" }}>
            {/* Sidebar */}
            <div
              style={{ width: "280px", minWidth: "280px", maxWidth: "280px" }}
              className="border-end"
            >
              <ul
                className="ps-3 create-lead-sidebar"
                style={{ listStyle: "none" }}
              >
                {getSidebarItems().map((item, index) => (
                  <li
                    key={item.id}
                    className="position-relative border-start ps-4 py-2"
                    style={{
                      cursor: "pointer",
                      opacity: item.show ? 1 : 0.5,
                      pointerEvents: item.show ? "auto" : "none",
                    }}
                  >
                    <span
                      className="position-absolute start-0 translate-middle rounded-circle"
                      style={{
                        width: 10,
                        height: 10,
                        top: index === 0 ? 20 : "50%",
                        backgroundColor: "rgb(7, 145, 255)",
                        border: "1px solid rgb(7, 145, 255)",
                      }}
                    />

                    <a
                      onClick={() => scrollToSection(item.id)}
                      style={{
                        color: "#676262",
                        textDecoration: "none",
                        fontSize: "14px",
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <Form
              className="form-container"
              onSubmit={handleSubmit(onSubmit)}
              style={{
                margin: 0,
                overflowY: "auto",
                height: "100%",
                paddingLeft: "20px",
                flex: 1,
                minWidth: 0,
                paddingBottom: "5rem",
                opacity: editId && isDataFetching ? 0.6 : 1,
              }}
              encType="multipart/form-data"
            >
              <div style={{ paddingTop: 10 }}>
                <h4
                  id="BasicInformation"
                  className="offcanvasSecHeading"
                  style={{ color: "rgb(7, 145, 255)", marginLeft: 0 }}
                >
                  General Information
                </h4>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter w-auto">
                        Contact Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="contact_name"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={
                              module_name === "company"
                                ? contactArray.map((contact) => ({
                                  value: contact.id,
                                  label: `${contact.first_name} ${contact.last_name}`,
                                }))
                                : customContactOptions
                            }
                            value={
                              contactId || watch("contact_name")
                                ? {
                                  value: contactId || watch("contact_name"),
                                  label:
                                    module_name === "company"
                                      ? (() => {
                                        const contact = contactArray.find(
                                          (c) =>
                                            c.id ===
                                            (contactId ||
                                              watch("contact_name")),
                                        )
                                        return contact
                                          ? `${contact.first_name} ${contact.last_name}`
                                          : "Select Contact Name"
                                      })()
                                      : customContactOptions.find(
                                        (c) =>
                                          c.value ===
                                          (contactId ||
                                            watch("contact_name")),
                                      )?.label || "Select Contact Name",
                                }
                                : null
                            }
                            onChange={(selectedOption) => {
                              const newValue = selectedOption
                                ? selectedOption.value
                                : ""

                              setValue("contact_name", newValue, {
                                shouldValidate: true,
                              })
                              setSelectedContactId(newValue)

                              if (newValue) {
                                clearErrors("contact_name")
                              }
                            }}
                            isSearchable={true}
                            isClearable={true}
                            styles={customStyles}
                            components={{
                              MenuList,
                              Option:
                                module_name !== "company"
                                  ? CustomOption
                                  : components.Option,
                            }}
                            show={(module_name === "company" || isEditMode) ? false : true}
                            canCreate={PERMISSION?.contact?.isCreate}
                            moduleType={"contact"}
                            onAddNew={() => {
                              setShowContact(true)
                              setLeadClose && setLeadClose(false)
                            }}
                            classNamePrefix="react-select"
                            placeholder="Select Contact Name"
                          />
                        )}
                      />
                      {errors.contact_name && (
                        <p className="text-danger fs-12 mt-1">
                          Contact Name is Required.
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter">
                        Pipeline <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="pipeline"
                        control={control}
                        rules={{ required: "Pipeline is required" }}
                        render={({ field }) => {
                          const selectedOption = availablePipelines.find(
                            (p) => String(p.id) === String(field.value),
                          )
                          return (
                            <Select
                              options={availablePipelines.map((pipeline) => ({
                                value: String(pipeline.id),
                                label: pipeline.pipeline_name,
                              }))}
                              value={
                                watch("pipeline")
                                  ? {
                                    value: watch("pipeline"),
                                    label: availablePipelines.find(
                                      (c) => c.id === watch("pipeline"),
                                    )?.pipeline_name,
                                  }
                                  : null
                              }
                              onChange={(selected) => {
                                const newPipelineId = selected
                                  ? Number(selected.value)
                                  : null
                                field.onChange(selected ? selected.value : null)
                                setSelectedPipelineId(newPipelineId)
                                // Reset pipeline stage when pipeline changes
                                setValue("pipeline_stage", null)
                                if (selected) {
                                  clearErrors("pipeline")
                                }
                              }}
                              isSearchable
                              isClearable
                              styles={customStyles}
                              placeholder="Select Pipeline"
                              classNamePrefix="react-select"
                            />
                          )
                        }}
                      />
                      {errors.pipeline && (
                        <p className="text-danger fs-12 mt-1">
                          Pipeline is required
                        </p>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter">
                        Pipeline Stage<span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="pipeline_stage"
                        control={control}
                        render={({ field }) => {
                          return (
                            <Select
                              options={pipeLine.map((stage) => ({
                                value: String(stage.id),
                                label: stage.stage_name,
                              }))}
                              value={
                                watch("pipeline_stage") !== null
                                  ? {
                                    value: String(watch("pipeline_stage")),
                                    label: pipeLine.find(
                                      (c) =>
                                        String(c.id) ===
                                        String(watch("pipeline_stage")),
                                    )?.stage_name,
                                  }
                                  : null
                              }
                              onChange={(selected) => {
                                field.onChange(selected ? selected.value : null)
                                if (selected) {
                                  clearErrors("pipeline_stage")
                                }
                              }}
                              isSearchable
                              styles={customStyles}
                              isClearable
                              placeholder={
                                selectedPipelineId
                                  ? "Select Pipeline Stage"
                                  : "Select Pipeline First"
                              }
                              classNamePrefix="react-select"
                              isDisabled={
                                !selectedPipelineId || pipeLine.length === 0
                              }
                            />
                          )
                        }}
                      />
                      {/* Show error even when disabled if pipeline is selected but stage is not */}
                      {errors.pipeline_stage && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.pipeline_stage.message}
                        </p>
                      )}
                      {/* Show helper text when pipeline is not selected */}
                      {!selectedPipelineId && (
                        <p
                          className="text-danger mt-1"
                          style={{ fontSize: "12px" }}
                        >
                          Please select a pipeline first
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <div className="row">
                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative focus-z-index">
                      <Form.Label className="fw-lighter">Owner</Form.Label>
                      <Controller
                        name="owner"
                        control={control}
                        rules={{ required: "Owner is required" }}
                        render={({ field }) => (
                          <Select
                            options={ownerOptions.map((owner) => ({
                              value: owner.id,
                              label: owner.label,
                            }))}
                            value={
                              watch("owner")
                                ? {
                                  value: watch("owner"),
                                  label:
                                    ownerOptions.find(
                                      (o) => o.id === watch("owner"),
                                    )?.label || "Select Owner",
                                }
                                : null
                            }
                            onChange={(selectedOption) => {
                              // If cleared, set to first owner
                              const newValue = selectedOption
                                ? selectedOption.value
                                : ownerOptions.length > 0
                                  ? ownerOptions[0].id
                                  : ""
                              setValue("owner", newValue)
                            }}
                            isDisabled={isEditMode ? !isOwnerSame : ""}
                            isSearchable={true}
                            isClearable={true}
                            classNamePrefix="react-select"
                            styles={{
                              placeholder: (provided) => ({
                                ...provided,
                                paddingLeft: "11px",
                              }),
                              ...customStyles,
                            }}
                            placeholder="Select Owner"
                          />
                        )}
                      />
                      {errors.owner && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.owner.message}
                        </p>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Expected Closure Date - 6 columns within this section */}
                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index ">
                      <Form.Label className="fw-lighter">
                        Expected Closure On <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        {...register("expected_closure_date")}
                        value={expectedDate || watch("expected_closure_date")}
                        onChange={(e) => {
                          setExpectedDate(e.target.value)
                          setValue("expected_closure_date", e.target.value)
                          if (e.target.value) {
                            clearErrors("expected_closure_date")
                          }
                        }}
                      />
                      {errors.expected_closure_date && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.expected_closure_date.message}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3 position-relative focus-z-index ">
                      <Form.Label className="fw-lighter">
                        Expected Closure Time <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="time"
                        {...register("expected_closure_time")}
                        value={expectedTime || watch("expected_closure_time")}
                        onChange={(e) => {
                          setExpectedTime(e.target.value)
                          setValue("expected_closure_time", e.target.value)
                          if (e.target.value) {
                            clearErrors("expected_closure_time")
                          }
                        }}
                      />
                      {errors.expected_closure_time && (
                        <p className="text-danger fs-12 mt-1">
                          {errors.expected_closure_time.message}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  {/* Date and Time Fields - 6 columns total */}
                </div>
              </div>
              <hr />
              {isContactSelected && !showExtraSections && (
                <>
                  {/* Communication Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4
                      id="Communication"
                      className="offcanvasSecHeading"
                      style={{ color: "rgb(7, 145, 255)", marginLeft: 0 }}
                    >
                      Communication
                    </h4>

                    <div className=" d-flex flex-column">
                      <div className="row">
                        {/* Primary Email */}
                        <div className="col-md-6">
                          <Form.Group
                            className="mb-3 col-md-12 position-relative focus-z-index"
                            controlId="formEmail"
                          >
                            <Form.Label className="w-auto">
                              Email<span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Form.Select
                                value={
                                  JSON.parse(
                                    contactDetails?.user_email ?? "[]",
                                  )[0]?.email_type || "Office"
                                }
                                style={{ width: "30%", fontSize: "14px" }}
                                disabled
                              >
                                <option value="Office">Office</option>
                                <option value="Personal">Personal</option>
                              </Form.Select>

                              <Form.Control
                                type="email"
                                value={
                                  JSON.parse(
                                    contactDetails?.user_email ?? "[]",
                                  )[0]?.email || ""
                                }
                                placeholder="ex: abc@gmail.com"
                                {...register("primary_email")}
                                disabled
                              />
                            </div>
                            {errors.primary_email && (
                              <p className="text-danger fs-12 mt-1">
                                {errors.primary_email.message}
                              </p>
                            )}
                          </Form.Group>
                        </div>

                        {/* Phone */}
                        <div className="col-md-6">
                          <Form.Group
                            className="mb-3 position-relative focus-z-index"
                            controlId="formContact"
                          >
                            <Form.Label className="w-auto">
                              Contact <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Form.Select
                                value={
                                  JSON.parse(
                                    contactDetails?.user_phone ?? "[]",
                                  )[0]?.phone_type || "Mobile"
                                }
                                style={{ width: "30%", fontSize: "14px" }}
                                disabled
                              >
                                <option value="Mobile">Mobile</option>
                                <option value="Work">Work</option>
                                <option value="Home">Home</option>
                                <option value="Personal">Personal</option>
                              </Form.Select>

                              <Controller
                                className="bg-primary"
                                control={control}
                                disabled
                                name="primary_contact_number"
                                render={({ field }) => {
                                  const phoneData =
                                    JSON.parse(
                                      contactDetails?.user_phone ?? "[]",
                                    )[0] || {}
                                  const fullNumber = `+${phoneData.country_code || "91"
                                    }${phoneData.phone || ""}`
                                  return (
                                    <PhoneInput
                                      disabled
                                      country={"in"}
                                      value={fullNumber}
                                      onChange={() => { }}
                                      inputProps={{
                                        readOnly: true,
                                      }}
                                      inputStyle={{
                                        width: "100%",
                                        height: "38px",
                                      }}
                                      countryCodeEditable={false}
                                      buttonStyle={{
                                        backgroundColor: "#e9ecef",
                                        border: "1px solid #ced4da",
                                        borderRight: "none",
                                        borderRadius: "4px 0 0 4px",
                                      }}
                                    />
                                  )
                                }}
                              />
                            </div>

                            {errors.primary_contact_number && (
                              <p className="text-danger fs-12 mt-1">
                                {errors.primary_contact_number.message}
                              </p>
                            )}
                          </Form.Group>
                        </div>
                      </div>

                      {/* Timezone */}
                      <div style={{ marginTop: "10px", height: "3rem" }}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formTimezone"
                        >
                          <Form.Label className="w-auto">
                            Select Timezone
                          </Form.Label>
                          <Controller
                            name="timezone"
                            control={control}
                            render={({ field }) => (
                              <TimezoneSelect
                                value={JSON.parse(
                                  contactDetails?.timezone ?? "{}",
                                )}
                                onChange={() => { }}
                                isDisabled={true}
                                timezones={{
                                  ...allTimezones,
                                  "America/Lima": "Pittsburgh",
                                  "Europe/Berlin": "Frankfurt",
                                }}
                                styles={customStyles}
                              />
                            )}
                          />
                          {errors.timezone && (
                            <p className="text-danger fs-12 mt-1">
                              {errors.timezone.message}
                            </p>
                          )}
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                  <hr />

                  {/* Location Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4
                      id="Location"
                      className="offcanvasSecHeading"
                      style={{ color: "rgb(7, 145, 255)", marginLeft: 0 }}
                    >
                      Location
                    </h4>

                    <Form.Group
                      className="mb-3 d-flex flex-column position-relative focus-z-index"
                      controlId="formAddress"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="w-auto"
                      >
                        Address
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as="textarea"
                          value={contactDetails?.address || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </InputGroup>
                    </Form.Group>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCountryReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Country
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={getCountryName(contactDetails?.country) || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formStateReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          State
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={getStateName(contactDetails?.state) || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>
                    </div>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCityReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          City
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={getCityName(contactDetails?.city) || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formZipcodeReadOnly"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Zipcode
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={contactDetails?.zipcode || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <hr />

                  {/* Social Links Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4
                      id="Social"
                      className="offcanvasSecHeading"
                      style={{ color: "rgb(7, 145, 255)", marginLeft: 0 }}
                    >
                      Social
                    </h4>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formFacebook"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Facebook
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.facebook || ""}
                            {...register("facebook")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formLinkedin"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          LinkedIn
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.linkedin || ""}
                            {...register("linkedin")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </div>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formInstagram"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Instagram
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.instagram || ""}
                            {...register("instagram")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formTwitter"
                      >
                        <Form.Label
                          style={{ marginBottom: 1, marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Twitter
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter URL"
                            value={contactDetails?.twitter || ""}
                            {...register("twitter")}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </div>
                  </div>
                  <hr />

                  {/* Business Information Section */}
                  <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                    <h4
                      id="BussinessInformation"
                      className="offcanvasSecHeading"
                      style={{ color: "rgb(7, 145, 255)", marginLeft: 0 }}
                    >
                      Business Information
                    </h4>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCompanyName"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Company Name
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.company_name || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formIndustry"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Company Industry
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.industry || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formBusinessType"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Business Type
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.business_type || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCompanyWebsite"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Company Website
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={contactDetails?.website || ""}
                            readOnly
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group
                          className="mb-3 col-md-12 position-relative focus-z-index"
                          controlId="formEmail"
                        >
                          <Form.Label className="w-auto">Email</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              disabled
                              value={
                                JSON.parse(
                                  contactDetails?.user_email ?? "[]",
                                )[0]?.email_type || "Office"
                              }
                              style={{
                                width: "30%",
                                fontSize: "14px",
                                backgroundColor: "#e9ecef",
                              }}
                            >
                              <option value="Office">Office</option>
                            </Form.Select>
                            <Form.Control
                              type="email"
                              value={
                                JSON.parse(
                                  contactDetails?.user_email ?? "[]",
                                )[0]?.email || ""
                              }
                              readOnly
                              style={{ backgroundColor: "#e9ecef" }}
                            />
                          </div>
                        </Form.Group>
                      </div>

                      <div className="col-md-6">
                        <Form.Group
                          className="mb-3 position-relative focus-z-index"
                          controlId="formContact"
                        >
                          <Form.Label className="w-auto">Contact</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              disabled
                              value="Mobile"
                              style={{
                                width: "30%",
                                fontSize: "14px",
                              }}
                            >
                              <option value="Mobile">Mobile</option>
                            </Form.Select>
                            <PhoneInput
                              disabled
                              country={"in"}
                              value={contactDetails?.company_phones || ""}
                              inputStyle={{ width: "100%", height: "38px" }}
                              countryCodeEditable={false}
                              inputProps={{ readOnly: true }}
                              buttonStyle={{
                                backgroundColor: "#e9ecef",
                                border: "1px solid #ced4da",
                                borderRight: "none",
                                borderRadius: "4px 0 0 4px",
                              }}
                            />
                          </div>
                        </Form.Group>
                      </div>
                    </div>

                    <Form.Group
                      className="mb-3 d-flex flex-column position-relative focus-z-index"
                      controlId="formAddress"
                    >
                      <Form.Label className="w-auto">
                        Company Address
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as="textarea"
                          value={contactDetails?.company_address || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </InputGroup>
                    </Form.Group>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCountry"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Country
                        </Form.Label>
                        <Form.Select
                          value={contactDetails?.company_country || ""}
                          disabled
                          style={{ backgroundColor: "#e9ecef" }}
                        >
                          <option value=""></option>
                          {country.map((c) => (
                            <option key={c.countryId} value={c.countryId}>
                              {c.countryName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formState"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          State
                        </Form.Label>
                        <Form.Select
                          value={contactDetails?.company_state || ""}
                          disabled
                          style={{ backgroundColor: "#e9ecef" }}
                        >
                          <option value=""></option>
                          {states.map((s, index) => (
                            <option key={index} value={s.stateCode}>
                              {s.stateName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>

                    <div className="row">
                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formCity"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          City
                        </Form.Label>
                        <Form.Select
                          value={contactDetails?.company_city || ""}
                          disabled
                          style={{ backgroundColor: "#e9ecef" }}
                        >
                          <option value=""></option>
                          {city.map((c, index) => (
                            <option key={index} value={c.cityCode}>
                              {c.cityName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group
                        className="mb-3 col-md-6 position-relative focus-z-index"
                        controlId="formZipcode"
                      >
                        <Form.Label
                          style={{ marginLeft: "11px" }}
                          className="w-auto"
                        >
                          Zipcode
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={contactDetails?.company_zipcode || ""}
                          readOnly
                          style={{ backgroundColor: "#e9ecef" }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <hr />

                  {/* Requirement Section */}
                </>
              )}
              {isContactSelected && (
                <>
                  <h4
                    id="Requirment"
                    className="offcanvasSecHeading"
                    style={{ color: "rgb(7, 145, 255)", marginLeft: 0 }}
                  >
                    Requirement
                  </h4>
                  <Form.Group
                    className="mb-3 position-relative focus-z-index"
                    controlId="requirement"
                  >
                    <Form.Label className="w-auto">
                      Requirement
                      {/* <span className="text-danger">*</span> */}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter requirement"
                      {...register("requirement", {
                        required: "Requirement is required",
                      })}
                    />
                    {errors.requirement && (
                      <span className="text-danger fs-12">
                        {errors.requirement.message}
                      </span>
                    )}
                  </Form.Group>

                  {/* Dynamic Product Fields */}
                  <DynamicProductFields
                    ref={productFieldsRef}
                    control={control}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                    clearErrors={clearErrors}
                    trigger={trigger}
                    errors={errors}
                    products={products}
                    setShowModal={setShowModal}
                    leadMasterGrant={leadMasterGrant}
                  />
                  <hr />

                  {!isEditMode && (
                    <>
                      <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                        <h4
                          id="FollowUpInformation"
                          className="offcanvasSecHeading"
                          style={{
                            color: "rgb(7, 145, 255)",
                            marginLeft: 0,
                          }}
                        >
                          FollowUp Information
                        </h4>

                        <div className="row">
                          <Form.Group
                            className="mb-3 col-md-3 position-relative focus-z-index"
                            controlId="formLeadSource"
                          >
                            <Form.Label
                              className="w-auto"
                              style={{ marginBottom: 1, marginLeft: "11px" }}
                            >
                              Lead Source <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                              name="lead_source"
                              control={control}
                              // rules={{
                              //   required: "lead_source is required",
                              // }}
                              render={({ field }) => (
                                <Select
                                  className="basic-single"
                                  options={leadSource.map((source) => ({
                                    value: source.id,
                                    label: source.source_title,
                                  }))}
                                  value={
                                    watch("lead_source")
                                      ? {
                                        value: watch("lead_source"),
                                        label:
                                          leadSource.find(
                                            (l) =>
                                              l.id === watch("lead_source"),
                                          )?.source_title ||
                                          "Select Lead Source",
                                      }
                                      : null
                                  }
                                  onChange={(selectedOption) => {
                                    const newValue = selectedOption
                                      ? selectedOption.value
                                      : ""
                                    setValue("lead_source", newValue)
                                    clearErrors("lead_source")
                                  }}
                                  isSearchable={true}
                                  isClearable={true}
                                  styles={customStyles}
                                  components={{ MenuList }}
                                  editMode={false}
                                  show={true}
                                  onAddNew={() => setSourceShow(true)}
                                  classNamePrefix="react-select"
                                  placeholder="Select lead source"
                                  // props for permission of lead source creation
                                  canCreate={PERMISSION.setting.feature.master.isCreate}
                                  moduleType={"lead_source"}
                                />
                              )}
                            />
                            {errors.lead_source && (
                              <span className="text-danger fs-12">
                                {errors.lead_source.message}
                              </span>
                            )}
                          </Form.Group>
                          <Form.Group
                            className="mb-3 col-md-3 position-relative focus-z-index"
                            controlId="formPriority"
                          >
                            <Form.Label
                              className="w-auto"
                              style={{ marginBottom: 1, marginLeft: "11px" }}
                            >
                              Priority
                            </Form.Label>
                            <Controller
                              name="priority"
                              control={control}
                              rules={{ required: "Priority is required" }}
                              render={({ field: { onChange, value, ref } }) => (
                                <Select
                                  inputRef={ref}
                                  options={priorityOptions.map((p) => ({
                                    value: p.id,
                                    label: p.label,
                                  }))}
                                  value={
                                    priorityOptions
                                      .map((p) => ({
                                        value: p.id,
                                        label: p.label,
                                      }))
                                      .find((opt) => opt.value === value) ||
                                    null
                                  }
                                  onChange={(val) =>
                                    onChange(val ? val.value : "")
                                  }
                                  isSearchable={true}
                                  classNamePrefix="react-select"
                                  styles={{
                                    placeholder: (provided) => ({
                                      ...provided,
                                      paddingLeft: "11px",
                                    }),
                                    ...customStyles,
                                  }}
                                  placeholder="Select Priority"
                                />
                              )}
                            />
                            {errors.priority && (
                              <p className="text-danger fs-12 mt-1">
                                {errors.priority.message}
                              </p>
                            )}
                          </Form.Group>

                          <Form.Group
                            className="mb-3 col-md-3 position-relative focus-z-index"
                            controlId="formFollowUp"
                          >
                            <Form.Label
                              className="w-auto"
                              style={{ marginBottom: 1, marginLeft: "11px" }}
                            >
                              Next Follow-up Date{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="date"
                              {...register("follow_up_date", {
                                required: "Follow-up date is required",
                              })}
                            />
                            {errors.follow_up_date && (
                              <span className="text-danger fs-12">
                                {errors.follow_up_date.message}
                              </span>
                            )}
                          </Form.Group>
                          <Form.Group
                            className="mb-3 col-md-3 position-relative focus-z-index"
                            controlId="formFollowUp"
                          >
                            <Form.Label
                              className="w-auto"
                              style={{ marginBottom: 1, marginLeft: "11px" }}
                            >
                              Next Follow-up Time{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="time"
                              {...register("follow_up_time", {
                                required: "Follow-up time is required",
                              })}
                            />
                            {errors.follow_up_time && (
                              <span className="text-danger fs-12">
                                {errors.follow_up_time.message}
                              </span>
                            )}
                          </Form.Group>
                        </div>

                        <Form.Group
                          controlId="formFeedback"
                          className="position-relative focus-z-index"
                        >
                          <Form.Label className="w-auto">
                            Feedback<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            {...register("feedback", {
                              required: "Feedback is required",
                              minLength: {
                                value: 5,
                                message:
                                  "Feedback is too short (minimum 5 characters)",
                              },
                            })}
                          />
                          {errors.feedback && (
                            <span className="text-danger fs-12">
                              {errors.feedback.message}
                            </span>
                          )}
                        </Form.Group>
                      </div>
                    </>
                  )}
                </>
              )}
              <div
                className="sticky-buttons"
                style={{
                  position: "fixed",
                  width: "calc(75% - 300px)",
                  bottom: 0,
                  right: 0,
                  height: "80px",
                  zIndex: 1,
                  backgroundColor: "white",
                  borderTop: "1px solid #dee2e6",
                  paddingRight: "20px",
                  paddingLeft: "20px",
                }}
              >
                <div className="d-flex justify-content-end gap-4 pt-4">
                  <CancelBtn
                    type={'button'}
                    onClick={handleClose}
                    submitClicked={isSaving}
                  />

                  {editId ? (
                    <SubmitBtn
                      type={'submit'}
                      loading={isSaving}
                      title={"Update"}
                    />
                  ) : (
                    <SubmitBtn
                      type={'submit'}
                      loading={isSaving}
                    />
                  )}
                </div>
              </div>
            </Form>
            {
              showModal &&
              <CreateProductsModal
                showModal={showModal}
                setShowModal={setShowModal}
                products={products}
                fetchProducts={fetchProducts}
                productFieldsRef={productFieldsRef}
              />
            }
          </div>
        </Offcanvas.Body>
      </Offcanvas>
      {
        <ContactForm
          onClose={() => setLeadClose(true)}
          show={contactShow}
          setShow={setShowContact}
          setRecentContactId={setRecentContact}
          country={country}
        ></ContactForm>
      }
      {
        sourceShow &&
        <UsersPopup
          setRecentSourceId={setRecentSource}
          sourceShow={sourceShow}
          setSourceShow={setSourceShow}
        />
      }
    </>
  )
}